// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ReturnOrder } from '@/return-order/api/types';
import {
  PHASE_CONFIG,
  ROStatus,
  isFullyReceived,
  parseStatus,
} from '@/return-order/constants/status';
import { useReturnOrderSearch } from '@/return-order/hooks/use-return-order-search';
import { Autocomplete, Box, Chip, Stack, TextField } from '@mui/material';
import { ForwardedRef, forwardRef, useEffect, useMemo, useRef } from 'react';

interface Props {
  /**
   * The name attribute for the form input element.
   * Used for form integration and accessibility.
   */
  name?: string;

  /**
   * The label text displayed above the input element.
   * Should clearly indicate the purpose of the search field.
   */
  label?: string;

  /**
   * If true, displays the component in an error state with red highlighting.
   * Used for form validation feedback.
   */
  error?: boolean;

  /**
   * Text displayed below the input element.
   * Used for additional context or error messages.
   */
  helperText?: string;

  /**
   * The currently selected return order in the search field.
   * Null indicates no selection.
   */
  value: ReturnOrder | null;

  /**
   * If true, the input field is disabled and cannot be interacted with.
   */
  disabled?: boolean;

  /**
   * Callback fired when a return order is selected or deselected.
   * Receives the selected return order or null if selection is cleared.
   */
  onChange: (value: ReturnOrder | null) => void;

  /**
   * Optional key to automatically resolve to a full return order.
   * When provided and value is null, the component will find the matching
   * return order and call onChange with it.
   */
  keyToResolve?: string;
}

/**
 * Returns the display order for a status, based on its phase.
 */
function getStatusDisplayOrder(status: ROStatus): number {
  const { phase } = parseStatus(status);
  return PHASE_CONFIG[phase].displayOrder;
}

/**
 * A component that renders a searchable return order selector with autocomplete.
 *
 * Features:
 * - Real-time search with fuzzy matching
 * - Shows return orders that can still take a shipment, grouped by status
 * - Form integration with error states
 * - Accessibility support via ARIA attributes
 * - Automatic resolution of return order by key
 *
 * Search behavior:
 * - Hides cancelled and fully received return orders
 * - Groups by status (Draft, Open, Shipped, etc.)
 * - Searches by RO number
 *
 * @param props - Component properties
 * @param ref - Forwarded ref to the input element
 */
function ReturnOrderSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { returnOrders, isLoading } = useReturnOrderSearch({});

  // Extract props for the filter and the resolve effects
  const { value, onChange, keyToResolve } = props;

  // A cancelled or fully received order takes no further shipments. The order
  // this shipment already names stays listed so the field can still show it.
  const filteredAndSortedROs = useMemo(() => {
    return returnOrders
      .filter(
        (ro) =>
          ro.id === value?.id ||
          ro.key === keyToResolve ||
          (ro.status !== 'CANCELLED' && !isFullyReceived(ro.status))
      )
      .sort(
        (a, b) =>
          getStatusDisplayOrder(a.status) - getStatusDisplayOrder(b.status)
      );
  }, [returnOrders, value, keyToResolve]);

  // Track the last key we resolved to prevent infinite loops
  const lastResolvedKey = useRef<string | null>(null);

  // Resolve keyToResolve to a full return order when value is null/empty
  // This runs when:
  // 1. keyToResolve is provided
  // 2. value is null OR value doesn't match the keyToResolve
  // 3. We haven't already resolved this specific key
  // 4. The RO list has data
  useEffect(() => {
    // Skip if no keyToResolve
    if (!keyToResolve) {
      return;
    }

    // Skip if we already resolved this key
    if (lastResolvedKey.current === keyToResolve) {
      return;
    }

    // Skip if value is already set to the correct RO
    if (value && value.key === keyToResolve) {
      lastResolvedKey.current = keyToResolve;
      return;
    }

    // Skip if no data loaded yet
    if (filteredAndSortedROs.length === 0) {
      return;
    }

    // Find the return order by key
    const match = filteredAndSortedROs.find((ro) => ro.key === keyToResolve);

    if (match) {
      lastResolvedKey.current = keyToResolve;
      onChange(match);
    }
  }, [filteredAndSortedROs, keyToResolve, value, onChange]);

  // Auto-resolve stub values to full records after data loads
  // A stub is a value with id/key set but returnOrderNumber empty (indicating incomplete data)
  useEffect(() => {
    // Skip if no value or value is already complete (has returnOrderNumber)
    if (!value || value.returnOrderNumber) {
      return;
    }

    // Value is a stub - find the full record by id or key
    const match = filteredAndSortedROs.find(
      (ro) => ro.id === value.id || ro.key === value.key
    );

    if (match) {
      onChange(match);
    }
  }, [filteredAndSortedROs, value, onChange]);

  return (
    <Autocomplete
      ref={ref}
      size="small"
      value={props.value}
      options={filteredAndSortedROs}
      loading={isLoading}
      loadingText="Loading..."
      disabled={props.disabled}
      getOptionLabel={(option) => option.returnOrderNumber}
      groupBy={(option) => PHASE_CONFIG[parseStatus(option.status).phase].label}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(optionProps, option) => (
        <Box component="li" {...optionProps} key={option.id}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <span>{option.returnOrderNumber}</span>
            {option.thirdParty.isEnabled && <Chip label="3PL" size="small" />}
          </Stack>
        </Box>
      )}
      onChange={(_, value) => props.onChange(value)}
      renderInput={(params) => (
        <TextField
          {...params}
          name={props.name}
          label={props.label}
          error={props.error}
          helperText={isLoading ? 'Loading...' : props.helperText}
        />
      )}
    />
  );
}

/**
 * A controlled component for searching and selecting return orders.
 * Wraps the ReturnOrderSearchRenderer with ref forwarding for form integration.
 * See the renderer documentation for full feature details.
 */
export const ReturnOrderSearch = forwardRef(ReturnOrderSearchRenderer);
